"use client";

// Muted, looping Mux video for the working-capital page visuals. Chromeless
// (no controls), plays only while on screen, and stays on the poster frame for
// visitors who prefer reduced motion.

import type { MuxPlayerRefAttributes } from "@mux/mux-player-react";
import * as React from "react";

import { MuxPlayerEager } from "@/components/post-rich-text/mux-player-eager";
import { cn } from "@/lib/utils";

interface LoopVideoProps {
  playbackId: string;
  /** Accessible description of what the loop shows. */
  title: string;
  /** CSS object-position for the cover crop. Defaults to the top-left corner. */
  objectPosition?: string;
  /** Background shown behind the video before it loads. */
  backgroundColor?: string;
  /** Extra classes for the player element, e.g. a zoom. */
  playerClassName?: string;
  className?: string;
}

export function LoopVideo({
  playbackId,
  title,
  objectPosition = "left top",
  backgroundColor = "#f5f5f5",
  playerClassName,
  className,
}: LoopVideoProps) {
  const wrapper = React.useRef<HTMLDivElement>(null);
  const player = React.useRef<MuxPlayerRefAttributes>(null);
  const visible = React.useRef(false);
  const reducedMotion = React.useRef(false);

  // The player doesn't reliably reflect the `muted`/`loop` props onto the
  // underlying media element, so set them directly before every play.
  const play = React.useCallback(() => {
    const media = player.current;
    if (!media || reducedMotion.current || !visible.current) return;
    media.muted = true;
    media.loop = true;
    // Autoplay can be refused (e.g. data saver); the poster frame stays.
    void media.play().catch(() => undefined);
  }, []);

  React.useEffect(() => {
    const el = wrapper.current;
    if (!el) return;
    reducedMotion.current = window.matchMedia(
      "(prefers-reduced-motion: reduce)"
    ).matches;
    if (reducedMotion.current) return;

    const observer = new IntersectionObserver(([entry]) => {
      visible.current = Boolean(entry?.isIntersecting);
      if (visible.current) play();
      else player.current?.pause();
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, [play]);

  const style = {
    width: "100%",
    height: "100%",
    aspectRatio: "auto",
    "--controls": "none",
    "--media-object-fit": "cover",
    "--media-object-position": objectPosition,
    "--media-background-color": backgroundColor,
  } as React.CSSProperties;

  return (
    <div
      ref={wrapper}
      className={cn("pointer-events-none overflow-hidden", className)}
    >
      <MuxPlayerEager
        ref={player}
        chromeless
        playbackId={playbackId}
        title={title}
        muted
        loop
        playsInline
        preload="auto"
        streamType="on-demand"
        minResolution="720p"
        style={style}
        className={cn("size-full origin-top-left", playerClassName)}
        // The observer can fire before the player is ready, so also start
        // once it can play; and restart by hand if `loop` didn't take.
        onCanPlay={play}
        onEnded={() => {
          const media = player.current;
          if (!media) return;
          media.currentTime = 0;
          play();
        }}
      />
    </div>
  );
}
